import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Verified as VerifiedIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { RiderKYC } from '../../services';
import KycDocumentUpload from './KycDocumentUpload';
import { RiderKYCSubmission, riderService } from '../../services/riderService';

interface RiderKYCTabProps {
  riderId: string;
  kycDocuments: RiderKYC[];
  kycStatus: string;
  loading: boolean;
  onViewDocument: (url: string, title: string) => void;
  onVerifyDocument: (kyc: RiderKYC) => void;
  onRequestDocuments: () => void;
  onUploadDocument?: (data: RiderKYCSubmission) => Promise<void>;
  onKYCRefresh?: () => Promise<void>;
}

interface KYCData {
  aadhaar: string;
  pan: string;
}

interface KYCStatusSummary {
  aadhaarSuccessStatus: boolean;
  aadhaarVerifiedRemark: string | null;
  panSuccessStatus: boolean;
  panVerifiedMessage: string | null;
  AadhaaPanLinkStatus: boolean;
  AadhaaPanLinkMessage: string | null;
}

interface VerificationResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

const getDocumentTypeDisplay = (documentType: string): string => {
  const documentTypes: Record<string, string> = {
    'aadhaar': 'Aadhaar Card',
    'pan': 'PAN Card',
    'dl': 'Driving License',
    'selfie': 'Identity Selfie',
    'agreement': 'Signed Agreement',
    'rc': 'Registration Certificate',
  };
  return documentTypes[documentType.toLowerCase()] || documentType;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
    case 'verified':
    case 'delivered':
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
    case 'cancelled':
    case 'failed':
      return 'error';
    case 'processing':
    case 'picked_up':
      return 'info';
    default:
      return 'default';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-IN');
};

const RiderKYCTab: React.FC<RiderKYCTabProps> = ({
  riderId,
  kycDocuments,
  kycStatus,
  loading,
  onViewDocument,
  onVerifyDocument,
  onRequestDocuments,
  onUploadDocument,
  onKYCRefresh,
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [freshKycDocuments, setFreshKycDocuments] = useState<RiderKYC[]>(kycDocuments);
  
  // KYC Verification states
  const [kycDataDialogOpen, setKycDataDialogOpen] = useState(false);
  const [kycData, setKycData] = useState<KYCData | null>(null);
  const [kycStatusSummary, setKycStatusSummary] = useState<KYCStatusSummary | null>(null);
  const [loadingKycData, setLoadingKycData] = useState(false);
  const [loadingVerification, setLoadingVerification] = useState<string | null>(null);
  const [verificationResponse, setVerificationResponse] = useState<VerificationResponse | null>(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [loadingStatusSummary, setLoadingStatusSummary] = useState(false);
  const [statusSummaryError, setStatusSummaryError] = useState<string | null>(null);

  // Fetch KYC Status Summary - Define FIRST before useEffect
  const fetchKYCStatusSummary = useCallback(async () => {
    setLoadingStatusSummary(true);
    setStatusSummaryError(null);

    try {
      console.log(`[RiderKYCTab] Fetching KYC status summary for rider: ${riderId}`);
      const response = await fetch(`/api/v1/additional-apis/kyc-status-summary/${riderId}`);
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        const message = result?.message || result?.error || 'Unable to load KYC status summary. Please try again.';
        console.warn('[RiderKYCTab] API returned an error while fetching KYC status summary:', message);
        setStatusSummaryError(message);
        setKycStatusSummary(null);
        return;
      }

      if (!result?.success) {
        const message = result?.message || result?.error || 'Unable to load KYC status summary. Please try again.';
        console.warn('[RiderKYCTab] KYC status summary API returned failure:', message);
        setStatusSummaryError(message);
        setKycStatusSummary(null);
        return;
      }

      if (result.data) {
        console.log(`[RiderKYCTab] Setting KYC status summary:`, result.data);
        setKycStatusSummary(result.data);
        setStatusSummaryError(null);
      } else {
        const message = 'KYC status summary returned empty data. Please refresh.';
        console.warn('[RiderKYCTab] KYC status summary returned empty data');
        setStatusSummaryError(message);
        setKycStatusSummary(null);
      }
    } catch (error: any) {
      const message = error?.message || 'Unable to load KYC status summary. Please try again.';
      console.error('[RiderKYCTab] Error fetching KYC status summary:', message);
      setStatusSummaryError(message);
      setKycStatusSummary(null);
    } finally {
      setLoadingStatusSummary(false);
    }
  }, [riderId]);

  useEffect(() => {
    setFreshKycDocuments(kycDocuments);
  }, [kycDocuments]);

  useEffect(() => {
    // Auto-load KYC status summary on component mount or riderId change
    if (riderId) {
      console.log(`[RiderKYCTab] useEffect triggered for riderId: ${riderId}`);
      fetchKYCStatusSummary();
    }
  }, [riderId, fetchKYCStatusSummary]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      console.log('[RiderKYCTab] Auto-refreshing presigned URLs (50min interval)');
      refreshPresignedUrls();
    }, 50 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [riderId]);

  const refreshPresignedUrls = useCallback(async () => {
    try {
      console.log(`[RiderKYCTab] Fetching fresh presigned URLs for rider ${riderId}`);
      const response = await riderService.getRiderKYC(riderId);
      if (response.success && response.data) {
        console.log(`[RiderKYCTab] Refreshed ${response.data.length} documents with new presigned URLs`);
        setFreshKycDocuments(response.data);
        if (onKYCRefresh) {
          await onKYCRefresh();
        }
      }
    } catch (error) {
      console.error('[RiderKYCTab] Error refreshing presigned URLs:', error);
    }
  }, [riderId, onKYCRefresh]);

  const handleViewDocument = useCallback(async (documentType: string, currentUrl: string, title: string) => {
    try {
      console.log(`[RiderKYCTab] Fetching fresh presigned URL before viewing ${documentType}`);
      const response = await riderService.getRiderKYC(riderId);
      if (response.success && response.data) {
        const freshDoc = response.data.find(doc => doc.documentType === documentType);
        if (freshDoc && freshDoc.documentImageUrl) {
          console.log(`[RiderKYCTab] Using fresh presigned URL for ${documentType}`);
          onViewDocument(freshDoc.documentImageUrl, title);
          setFreshKycDocuments(response.data);
        } else {
          console.warn(`[RiderKYCTab] No fresh URL found for ${documentType}, using current URL`);
          onViewDocument(currentUrl, title);
        }
      } else {
        console.warn('[RiderKYCTab] Failed to fetch fresh URLs, using current URL');
        onViewDocument(currentUrl, title);
      }
    } catch (error) {
      console.error('[RiderKYCTab] Error fetching fresh presigned URL:', error);
      onViewDocument(currentUrl, title);
    }
  }, [riderId, onViewDocument]);

  const handleUploadDocument = async (data: RiderKYCSubmission) => {
    if (onUploadDocument) {
      await onUploadDocument(data);
      setUploadDialogOpen(false);
      await refreshPresignedUrls();
    }
  };

  // Fetch KYC Data (Aadhaar and PAN)
  const fetchKYCData = async () => {
    setLoadingKycData(true);
    try {
      const response = await fetch(`/api/v1/additional-apis/kyc-data/${riderId}`);
      const result = await response.json();
      if (result.success && result.data) {
        setKycData(result.data);
      }
      // Also fetch status summary
      await fetchKYCStatusSummary();
    } catch (error) {
      console.error('Error fetching KYC data:', error);
      setVerificationResponse({
        success: false,
        error: 'Failed to fetch KYC data',
      });
      setResponseDialogOpen(true);
    } finally {
      setLoadingKycData(false);
    }
  };

  // Verify Aadhaar
  const handleAadhaarVerification = async () => {
    setLoadingVerification('aadhaar');
    try {
      const response = await fetch('/api/v1/additional-apis/aadhaar-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ riderId }),
      });
      const result = await response.json();
      setVerificationResponse(result);
      setResponseDialogOpen(true);
      // Refresh status summary after verification
      await fetchKYCStatusSummary();
    } catch (error: any) {
      setVerificationResponse({
        success: false,
        error: error.message || 'Aadhaar verification failed',
      });
      setResponseDialogOpen(true);
    } finally {
      setLoadingVerification(null);
    }
  };

  // Verify PAN
  const handlePanVerification = async () => {
    setLoadingVerification('pan');
    try {
      const response = await fetch('/api/v1/additional-apis/pan-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ riderId }),
      });
      const result = await response.json();
      setVerificationResponse(result);
      setResponseDialogOpen(true);
      await fetchKYCStatusSummary();
    } catch (error: any) {
      setVerificationResponse({
        success: false,
        error: error.message || 'PAN verification failed',
      });
      setResponseDialogOpen(true);
    } finally {
      setLoadingVerification(null);
    }
  };

  // Verify PAN-Aadhaar Link
  const handlePanAadhaarLinkVerification = async () => {
    setLoadingVerification('link');
    try {
      const response = await fetch('/api/v1/additional-apis/pan-aadhaar-link-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ riderId }),
      });
      const result = await response.json();
      setVerificationResponse(result);
      setResponseDialogOpen(true);
      await fetchKYCStatusSummary();
    } catch (error: any) {
      setVerificationResponse({
        success: false,
        error: error.message || 'PAN-Aadhaar link verification failed',
      });
      setResponseDialogOpen(true);
    } finally {
      setLoadingVerification(null);
    }
  };

  const handleOpenKycDataDialog = () => {
    setKycDataDialogOpen(true);
    fetchKYCData();
  };

  const handleCloseKycDataDialog = () => {
    setKycDataDialogOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading KYC documents...</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* KYC Status Summary Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">KYC Verification Status Summary</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={fetchKYCStatusSummary}
                disabled={loadingStatusSummary}
              >
                {loadingStatusSummary ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>

            {loadingStatusSummary && !kycStatusSummary ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {statusSummaryError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {statusSummaryError}
                  </Alert>
                )}

                {kycStatusSummary ? (
                  <Grid container spacing={2}>
                    {[
                      {
                        title: 'Aadhaar Verification',
                        status: kycStatusSummary.aadhaarSuccessStatus,
                        description: kycStatusSummary.aadhaarVerifiedRemark || 'Awaiting Aadhaar verification result.',
                      },
                      {
                        title: 'PAN Verification',
                        status: kycStatusSummary.panSuccessStatus,
                        description: kycStatusSummary.panVerifiedMessage || 'Awaiting PAN verification result.',
                      },
                      {
                        title: 'PAN-Aadhaar Link',
                        status: kycStatusSummary.AadhaaPanLinkStatus,
                        description: kycStatusSummary.AadhaaPanLinkMessage || 'Awaiting link verification result.',
                      },
                    ].map((item) => (
                      <Grid item xs={12} md={4} key={item.title}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            minHeight: 180,
                            // background: 'linear-gradient(180deg, #ffffff 0%, #f3f7ff 100%)',
                            boxShadow: '0 12px 35px rgba(15, 35, 80, 0.05)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, letterSpacing: '0.08em' }}>
                              {item.title.toUpperCase()}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                              {item.title}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip
                              icon={item.status ? <VerifiedIcon /> : undefined}
                              label={item.status ? 'Verified' : 'Pending'}
                              color={item.status ? 'success' : 'warning'}
                              size="small"
                              variant={item.status ? 'filled' : 'outlined'}
                            />
                            <Typography variant="body2" color="textSecondary">
                              {item.status ? 'Completed' : 'Needs attention'}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                            {item.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : !loadingStatusSummary ? (
                  <Alert severity="warning">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      ⚠️ KYC status summary is not available right now.
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      <li>Please verify the Rider ID: {riderId}</li>
                      <li>Confirm that the KYC service is reachable</li>
                    </Box>
                    <Button 
                      size="small" 
                      variant="contained" 
                      sx={{ mt: 2 }}
                      onClick={fetchKYCStatusSummary}
                    >
                      Retry
                    </Button>
                  </Alert>
                ) : null}
              </>
            )}

            {/* KYC Verification Action Buttons */}
            {kycStatusSummary && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                  Quick Actions
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenKycDataDialog}
                    startIcon={<VisibilityIcon />}
                    fullWidth
                  >
                    View KYC Data
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAadhaarVerification}
                    disabled={loadingVerification === 'aadhaar'}
                    fullWidth
                  >
                    {loadingVerification === 'aadhaar' ? 'Verifying...' : 'Verify Aadhaar'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePanVerification}
                    disabled={loadingVerification === 'pan'}
                    fullWidth
                  >
                    {loadingVerification === 'pan' ? 'Verifying...' : 'Verify PAN'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePanAadhaarLinkVerification}
                    disabled={loadingVerification === 'link'}
                    fullWidth
                  >
                    {loadingVerification === 'link' ? 'Verifying...' : 'Verify Link'}
                  </Button>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Dialog open={kycDataDialogOpen} onClose={handleCloseKycDataDialog} maxWidth="sm" fullWidth>
        <DialogTitle>KYC Data</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loadingKycData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : kycData ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Rider ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1rem', p: 1, borderRadius: 1 }}>
                  {riderId}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Aadhaar Number
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1rem', p: 1, borderRadius: 1 }}>
                  {kycData.aadhaar || 'Not provided'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                  PAN Number
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1rem', p: 1, borderRadius: 1 }}>
                  {kycData.pan || 'Not provided'}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Alert severity="warning">
              Failed to load KYC data. Please try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseKycDataDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Verification Response Dialog */}
      <Dialog open={responseDialogOpen} onClose={() => setResponseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {verificationResponse?.success ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4caf50' }}>
              <VerifiedIcon /> Verification Successful
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f44336' }}>
              <ErrorIcon /> Verification Failed
            </Box>
          )}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {verificationResponse?.success ? (
            <Alert severity="success">
              <Typography variant="body2">
                {verificationResponse.message || 'Verification completed successfully'}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="error">
              <Typography variant="body2">
                {verificationResponse?.error || 'Verification failed. Please try again.'}
              </Typography>
            </Alert>
          )}
          {verificationResponse?.data && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Response Details:
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ mt: 1, overflow: 'auto', maxHeight: 200, fontFamily: 'monospace', fontSize: '0.75rem' }}
              >
                {JSON.stringify(verificationResponse.data, null, 2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Original KYC Documents Section */}
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.25}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: '1.35rem', md: '1.5rem' }, fontWeight: 800 }}>
            KYC Documents
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              label={`${kycDocuments.length} Documents`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={kycStatus || 'Unknown'}
              color={getStatusColor(kycStatus || 'pending') as any}
              size="small"
            />
            {onUploadDocument && (
              <Button
                variant="contained"
                size="small"
                onClick={() => setUploadDialogOpen(true)}
                startIcon={<CloudUploadIcon />}
                sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
              >
                Upload Document
              </Button>
            )}
          </Box>
        </Stack>
      </Grid>

      {kycDocuments.length === 0 ? (
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              No KYC documents found for this rider.
            </Alert>
            <Typography variant="body1" paragraph>
              Required documents: Aadhaar Card, PAN Card, Driving License, Identity Selfie
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, flexDirection: { xs: 'column', sm: 'row' } }}>
              {onUploadDocument && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setUploadDialogOpen(true)}
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Upload Document
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={onRequestDocuments}
                startIcon={<AssignmentIcon />}
                fullWidth
              >
                Request KYC Documents
              </Button>
            </Box>
          </Card>
        </Grid>
      ) : (
        freshKycDocuments.map((kyc) => (
          <Grid item xs={12} md={6} key={kyc.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 800, overflowWrap: 'anywhere' }}>
                    {getDocumentTypeDisplay(kyc.documentType)}
                  </Typography>
                  <Chip
                    label={kyc.verificationStatus}
                    color={getStatusColor(kyc.verificationStatus) as any}
                    size="small"
                  />
                </Box>

                {kyc.documentImageUrl && kyc.documentType.toLowerCase() === 'selfie' && (
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      component="img"
                      src={kyc.documentImageUrl}
                      alt="Rider Selfie"
                      sx={{
                        width: { xs: 104, md: 120 },
                        height: { xs: 104, md: 120 },
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #eee',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9, border: '2px solid #3f51b5' },
                      }}
                      onClick={() =>
                        handleViewDocument(kyc.documentType, kyc.documentImageUrl || '', 'Rider Selfie')
                      }
                    />
                  </Box>
                )}

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Document Number
                    </Typography>
                    <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{kyc.documentNumber || 'Not provided'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Submitted Date
                    </Typography>
                    <Typography variant="body1">{formatDate(kyc.createdAt)}</Typography>
                  </Box>
                  {kyc.verificationDate && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Verification Date
                      </Typography>
                      <Typography variant="body1">{formatDate(kyc.verificationDate)}</Typography>
                    </Box>
                  )}
                  {kyc.verifiedBy && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Verified By
                      </Typography>
                      <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{kyc.verifiedBy}</Typography>
                    </Box>
                  )}
                  {kyc.verificationNotes && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Verification Notes
                      </Typography>
                      <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{kyc.verificationNotes}</Typography>
                    </Box>
                  )}

                  {!kyc.documentImageUrl || kyc.documentImageUrl.trim() === '' ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Document File Missing
                      </Typography>
                      <Typography variant="caption">
                        The document file is not available in storage. Please re-upload this document.
                      </Typography>
                    </Alert>
                  ) : null}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { flex: { xs: '1 1 100%', sm: '0 0 auto' } } }}>
                    {kyc.documentImageUrl && kyc.documentImageUrl.trim() !== '' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() =>
                          handleViewDocument(
                            kyc.documentType,
                            kyc.documentImageUrl || '',
                            getDocumentTypeDisplay(kyc.documentType)
                          )
                        }
                      >
                        View Document
                      </Button>
                    ) : null}
                    {kyc.verificationStatus === 'pending' && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => onVerifyDocument(kyc)}
                      >
                        Verify
                      </Button>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))
      )}

      {/* KYC Document Upload Dialog */}
      {onUploadDocument && (
       <KycDocumentUpload
  open={uploadDialogOpen}
  onClose={() => setUploadDialogOpen(false)}
  onSubmit={handleUploadDocument}
  documentCount={freshKycDocuments.length}
/>
      )}
    </Grid>
  );
};

export default RiderKYCTab;
